import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { RequireRole } from '@/components/RequireRole';
import { AppShell } from '@/components/AppShell';
import { homePathFor } from '@/lib/navigation';
import { SignInPage } from '@/pages/SignInPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';
import { AdminHome } from '@/pages/home/AdminHome';
import { OrgApprovalsPage } from '@/pages/admin/OrgApprovalsPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { TaxonomyPage } from '@/pages/admin/TaxonomyPage';
import { InvitesPage } from '@/pages/staff/InvitesPage';
import { ReportsPage } from '@/pages/admin/ReportsPage';
import { RegisterOrgPage } from '@/pages/guest/RegisterOrgPage';
import { NominatePage } from '@/pages/recommender/NominatePage';
import { MyNominationsPage } from '@/pages/recommender/MyNominationsPage';
import { NominationQueuePage } from '@/pages/recruiter/NominationQueuePage';
import { CandidatesPage } from '@/pages/recruiter/CandidatesPage';
import { CandidateDetailPage } from '@/pages/recruiter/CandidateDetailPage';
import { OpportunityReviewPage } from '@/pages/recruiter/OpportunityReviewPage';
import { MatchWorkspacePage } from '@/pages/recruiter/MatchWorkspacePage';
import { AssessmentPage } from '@/pages/org/AssessmentPage';
import { OpportunitiesPage } from '@/pages/org/OpportunitiesPage';
import { OpportunityBuilderPage } from '@/pages/org/OpportunityBuilderPage';
import { OrgMatchesPage } from '@/pages/org/OrgMatchesPage';
import { CandidateProfilePage } from '@/pages/candidate/CandidateProfilePage';
import { DiagnosticPage } from '@/pages/candidate/DiagnosticPage';
import { MyOpportunitiesPage } from '@/pages/candidate/MyOpportunitiesPage';

/**
 * Five trees plus a guest one, each guarded by the roles that may enter it.
 * Nothing is shared between trees, so a page cannot accidentally become
 * reachable by a role it was not written for.
 */
export default function App() {
  const { user, role, restoring } = useAuth();

  if (restoring) return <div data-testid="auth-restoring" />;

  return (
    <Routes>
      <Route path="/" element={user && role ? <Navigate to={homePathFor(role)} replace /> : <SignInPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      <Route path="/admin/*" element={
        <RequireRole allow={['admin']}>
          <AppShell>
            <Routes>
              <Route index element={<AdminHome />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="taxonomy" element={<TaxonomyPage />} />
              <Route path="invites" element={<InvitesPage />} />
              <Route path="orgs" element={<OrgApprovalsPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </AppShell>
        </RequireRole>
      } />

      <Route path="/recruiter/*" element={
        <RequireRole allow={['recruiter', 'admin']}>
          <AppShell>
            <Routes>
              <Route index element={<NominationQueuePage />} />
              <Route path="candidates" element={<CandidatesPage />} />
              <Route path="candidates/:id" element={<CandidateDetailPage />} />
              <Route path="opportunities" element={<OpportunityReviewPage />} />
              <Route path="matches" element={<MatchWorkspacePage />} />
              <Route path="invites" element={<InvitesPage />} />
              <Route path="*" element={<Navigate to="/recruiter" replace />} />
            </Routes>
          </AppShell>
        </RequireRole>
      } />

      <Route path="/recommender/*" element={
        <RequireRole allow={['recommender']}>
          <AppShell>
            <Routes>
              <Route index element={<NominatePage />} />
              <Route path="mine" element={<MyNominationsPage />} />
              <Route path="*" element={<Navigate to="/recommender" replace />} />
            </Routes>
          </AppShell>
        </RequireRole>
      } />

      <Route path="/org/*" element={
        <RequireRole allow={['ngo-rep']}>
          <AppShell>
            <Routes>
              <Route index element={<AssessmentPage />} />
              <Route path="opportunities" element={<OpportunitiesPage />} />
              <Route path="opportunities/:id" element={<OpportunityBuilderPage />} />
              <Route path="matches" element={<OrgMatchesPage />} />
              <Route path="*" element={<Navigate to="/org" replace />} />
            </Routes>
          </AppShell>
        </RequireRole>
      } />

      <Route path="/candidate/*" element={
        <RequireRole allow={['candidate']}>
          <AppShell>
            <Routes>
              <Route index element={<CandidateProfilePage />} />
              <Route path="diagnostic" element={<DiagnosticPage />} />
              <Route path="opportunities" element={<MyOpportunitiesPage />} />
              <Route path="*" element={<Navigate to="/candidate" replace />} />
            </Routes>
          </AppShell>
        </RequireRole>
      } />

      <Route path="/welcome" element={
        <RequireRole allow={['guest']}><AppShell><RegisterOrgPage /></AppShell></RequireRole>
      } />

      {/* A path nobody wrote sends people home rather than to a blank screen. */}
      <Route path="*" element={<Navigate to={homePathFor(role)} replace />} />
    </Routes>
  );
}
